/**
 * Who the ids on a survey row belong to — the roster, read as a map from customer id to a face.
 *
 * ── The COUNT and the FACES arrive separately, and that is deliberate ────────────────────────
 * `GET /survey/templates` carries `assignedClientCount` and `assignedCustomerIds`, so a card knows
 * how many people hold a form and which ones before any second read answers. What it does NOT carry
 * is their names or their photographs, and those come from the roster the screen already reads. Same
 * arrangement the plan library's assigned strip stands on: the count sizes the box, the map fills
 * it, and the two are allowed to disagree for the length of one request.
 *
 * ── The person is read off the wire, never composed ──────────────────────────────────────────
 * `entities/client` owns that reading — the backend composes the display name (falling back to the
 * e-mail address when a client has set none), draws the initials from it, and resolves the photo.
 * Composing any of the three again is what made one client read `AK` on one surface and `AM` on
 * another, so this module projects `ClientRow` and never touches the DTO underneath it.
 *
 * ── An offer has no customer id, and is therefore not here ───────────────────────────────────
 * `ClientRow.customerId` is null for an invitation to somebody who is not yet this trainer's
 * client, and a survey cannot be assigned to them. So that row simply has no key to be found
 * under, rather than being filtered out by a rule of this module's own. An invited roster ENTRY
 * has its id and its face, because work can be assigned to it before it is accepted (R2).
 *
 * No React, no DOM, no query hook, no i18next.
 */

import type { ClientRoster } from '~/entities/client';
import type { ClientFace } from '~/features/survey-question-editor';

/** The shape is the question editor's, where the avatar stack and the client picker live. */
export type { ClientFace };

export function clientFaces(roster: ClientRoster): ReadonlyMap<number, ClientFace> {
  const faces = new Map<number, ClientFace>();
  for (const row of roster.rows) {
    if (row.customerId === null) continue;
    faces.set(row.customerId, {
      customerId: row.customerId,
      name: row.fullName,
      initials: row.initials,
      avatarUrl: row.avatarUrl,
    });
  }
  return faces;
}

/** Just the names, which is all the search and the client facet need. */
export function clientNames(faces: ReadonlyMap<number, ClientFace>): ReadonlyMap<number, string> {
  return new Map([...faces].map(([id, face]) => [id, face.name]));
}
